import React, { useState, useEffect, useCallback, useRef } from 'react';
import { 
    ClipboardCheck, Check, X, 
    Beaker, Loader2, Send, Archive, FileText, AlertCircle, HelpCircle, RotateCcw, CheckCircle2
} from 'lucide-react';
import API, { socket } from '../../services/api';
import Sidebar from '../../components/Sidebar';

const VerificationMagasin = () => {
    // --- ÉTATS ---
    const [mode, setMode] = useState(localStorage.getItem('magasin_mode') || 'FLUX');
    const [lignes, setLignes] = useState([]); 
    const [compositionMP, setCompositionMP] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [loadingMP, setLoadingMP] = useState(false);
    const [observations, setObservations] = useState({});
    
    // État pour stocker les nombres par catégorie
    const [counts, setCounts] = useState({ FLUX: 0, VALIDES: 0, REJETES: 0, ARCHIVES: 0 });
    
    const analyseRef = useRef(null);

    // État de la notification Toast
    const [notification, setNotification] = useState({
        isOpen: false,
        type: 'success', 
        message: '',
        onConfirm: null,
        isLoading: false
    });

    const primaryColor = '#2563eb';
    const borderColor = '#e2e8f0';

    // --- 1. FONCTIONS UTILITAIRES ---
    const showToast = useCallback((message, type = 'success') => {
        setNotification({ isOpen: true, type, message, onConfirm: null, isLoading: false });
    }, []);

    const showConfirm = useCallback((message, onConfirm) => {
        setNotification({ isOpen: true, type: 'confirm', message, onConfirm, isLoading: false });
    }, []);

    const formaterDate = (dateString) => {
        if (!dateString) return "-";
        return new Date(dateString).toLocaleDateString('fr-FR', { 
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
        });
    };

    // --- 2. LOGIQUE DE RÉCUPÉRATION DES DONNÉES ---
    const fetchEtAplatirBesoins = useCallback(async () => {
        setLoading(true);
        try {
            const [resAttente, resFinalisees] = await Promise.all([
                API.get('/verification-magasin/en-attente').catch(() => ({ data: [] })),
                API.get('/verification-magasin/finalisees').catch(() => ({ data: [] }))
            ]);
            
            const groupes = mode === 'FLUX' ? (resAttente.data || []) : (resFinalisees.data || []);
            let lignesAffichage = [];

            await Promise.all(groupes.map(async (groupe) => {
                try {
                    const detailRes = await API.get(`/verification-magasin/details/${groupe.id}`);
                    if (Array.isArray(detailRes.data)) {
                        const lignesDuGroupe = detailRes.data.map(l => ({
                            ...l,
                            code_groupe_parent: groupe.id,
                            date_affichage: l.date_creation || groupe.date_creation 
                        }));
                        lignesAffichage = [...lignesAffichage, ...lignesDuGroupe];
                    }
                } catch (errDetail) {
                    console.error("Erreur détails groupe:", groupe.id);
                }
            }));

            const isEnAttente = (statut) => !statut || statut.replace('_', ' ').toUpperCase() === 'EN ATTENTE';
            let lignesFiltrees = [];
            switch (mode) {
                case 'FLUX': lignesFiltrees = lignesAffichage.filter(l => isEnAttente(l.statut_ligne)); break;
                case 'VALIDES': lignesFiltrees = lignesAffichage.filter(l => l.statut_ligne === 'VALIDE'); break;
                case 'REJETES': lignesFiltrees = lignesAffichage.filter(l => l.statut_ligne === 'REFUSE'); break;
                case 'ARCHIVES': lignesFiltrees = lignesAffichage.filter(l => l.statut_ligne === 'ARCHIVE' || l.statut_ligne === 'TERMINE'); break;
                default: lignesFiltrees = lignesAffichage;
            }

            lignesFiltrees.sort((a, b) => new Date(b.date_affichage) - new Date(a.date_affichage));
            setLignes(lignesFiltrees);
            setCounts(prev => ({
                ...prev,
                [mode]: lignesFiltrees.length
            }));

            const obsMap = {};
            lignesFiltrees.forEach(item => { obsMap[item.id] = item.observation_magasin || ""; });
            setObservations(obsMap);
        } catch (err) {
            console.error("Erreur chargement:", err);
        } finally {
            setLoading(false);
        }
    }, [mode]);

    // --- 3. EFFETS ---
    useEffect(() => {
        fetchEtAplatirBesoins();
        const handleGlobalUpdate = (event) => {
            const { table } = event.detail;
            if (['production_needs', 'products', 'psf', 'all'].includes(table)) {
                fetchEtAplatirBesoins();
            }
        };
        window.addEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
        return () => window.removeEventListener('ERP_DATA_CHANGED', handleGlobalUpdate);
    }, [mode, fetchEtAplatirBesoins]);

    useEffect(() => {
        if (notification.isOpen && notification.type !== 'confirm') {
            const timer = setTimeout(() => setNotification(prev => ({ ...prev, isOpen: false })), 4000);
            return () => clearTimeout(timer);
        }
    }, [notification.isOpen, notification.type]);

    // --- 4. ACTIONS ---
    const executerTraitement = async (idLigne, statutFinal) => {
        setNotification(prev => ({ ...prev, isLoading: true }));
        try {
            await API.put(`/verification-magasin/ligne/${idLigne}`, {
                statut: statutFinal,
                observation_magasin: observations[idLigne] || ""
            });
            setNotification({ isOpen: false });
            setCompositionMP([]); 
            fetchEtAplatirBesoins();
            showToast("Action enregistrée avec succès.");
        } catch (err) {
            setNotification({ isOpen: false });
            showToast("La mise à jour a échoué.", "error");
        }
    };

    const traiterLigne = async (idLigne, action) => {
        const statutFinal = action === 'VALIDER' ? 'VALIDE' : 'REFUSE';
        if (action === 'VALIDER') {
            try {
                const resAnalyse = await API.get(`/verification-magasin/analyse/${idLigne}`);
                const analyse = Array.isArray(resAnalyse.data) ? resAnalyse.data : [];
                const articlesManquants = analyse.filter(mp => parseFloat(mp.ecart) < 0);
                setCompositionMP(analyse);
                setTimeout(() => analyseRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);

                if (articlesManquants.length > 0) {
                    showConfirm(
                        `⚠️ Stock insuffisant pour ${articlesManquants.length} composant(s). Le stock dispo sera puisé à 0 et le manque basculé en régularisation d'attente. Continuer ?`, 
                        () => executerTraitement(idLigne, statutFinal)
                    );
                } else {
                    showConfirm("Valider et réserver les stocks pour ce besoin ?", () => executerTraitement(idLigne, statutFinal));
                }
            } catch (err) {
                showToast("Impossible d'analyser les stocks.", "error");
            }
        } else {
            showConfirm("Rejeter ce besoin de production ?", () => executerTraitement(idLigne, statutFinal));
        }
    };

    const annulerValidation = (idLigne) => {
        showConfirm("Annuler la validation et libérer le stock réservé ?", async () => {
            setNotification(prev => ({ ...prev, isLoading: true }));
            try {
                await API.put(`/verification-magasin/annuler-validation/${idLigne}`);
                setNotification({ isOpen: false });
                fetchEtAplatirBesoins();
                showToast("Validation annulée.");
            } catch (err) {
                setNotification({ isOpen: false });
                showToast("L'annulation a échoué.", "error");
            }
        });
    };

    const majObservation = async (idLigne, statutActuel) => {
        try {
            await API.put(`/verification-magasin/ligne/${idLigne}`, {
                statut: statutActuel || 'EN_ATTENTE', 
                observation_magasin: observations[idLigne] || ""
            });
            showToast("Note enregistrée.");
        } catch (err) {
            showToast("Erreur d'enregistrement.", "error");
        }
    };

    const analyserMPLigne = async (idLigne) => {
        setLoadingMP(true);
        setCompositionMP([]); 
        try {
            const res = await API.get(`/verification-magasin/analyse/${idLigne}`);
            setCompositionMP(Array.isArray(res.data) ? res.data : []);
            setTimeout(() => analyseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 100);
        } catch (err) {
            showToast("Analyse impossible.", "error");
        } finally {
            setLoadingMP(false);
        }
    };

    // --- 5. COMPOSANTS DE RENDU INTERNES ---
    const NotificationToast = () => {
        if (!notification.isOpen) return null;
        const isConfirm = notification.type === 'confirm';
        const color = notification.type === 'error' ? '#ef4444' : (isConfirm ? '#f59e0b' : '#10b981');
        return (
            <div style={{ 
                position: 'absolute', top: '20px', left: '50%', transform: 'translateX(-50%)', 
                zIndex: 3000, display: 'flex', alignItems: 'center', background: 'white', 
                padding: '12px 20px', borderRadius: '15px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)', 
                border: `1px solid ${borderColor}`, minWidth: '400px', borderLeft: `8px solid ${color}` 
            }}>
                <div style={{ 
                    display: 'flex', justifyContent: 'center', alignItems: 'center', 
                    width: '35px', height: '35px', borderRadius: '50%', 
                    background: `${color}15`, marginRight: '15px' 
                }}>
                    {notification.type === 'error' ? <AlertCircle size={20} color={color} /> : 
                     isConfirm ? <HelpCircle size={20} color={color} /> : <CheckCircle2 size={20} color={color} />}
                </div>
                <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontWeight: '700', color: '#1e293b', fontSize: '13px' }}>{notification.message}</p>
                    {isConfirm && (
                        <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                            <button disabled={notification.isLoading} onClick={notification.onConfirm} 
                                style={{ background: primaryColor, color: 'white', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}>
                                {notification.isLoading ? '...' : 'CONFIRMER'}
                            </button>
                            <button onClick={() => setNotification({isOpen: false})} 
                                style={{ background: '#f1f5f9', color: '#64748b', border: 'none', padding: '6px 15px', borderRadius: '6px', fontWeight: '800', cursor: 'pointer', fontSize: '11px' }}>
                                ANNULER
                            </button>
                        </div>
                    )}
                </div>
                {!isConfirm && (
                    <button onClick={() => setNotification({isOpen: false})} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#cbd5e1' }}>
                        <X size={20} />
                    </button>
                )}
            </div>
        );
    };

    return (
        <div style={{ display: 'flex', width: '100vw', height: '100vh', overflow: 'hidden', background: '#f8fafc', position: 'relative' }}>
            <Sidebar />
            <NotificationToast />
            <div style={{ flex: 1, padding: '20px', display: 'flex', flexDirection: 'column', gap: '20px', maxWidth: 'calc(100vw - 260px)', height: '100vh', overflowY: 'auto' }}>
                
                <div style={{ background: 'white', padding: '15px 25px', borderRadius: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', flexShrink: 0 }}>
                    <h2 style={{ margin: 0, color: '#1e293b', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '18px' }}>
                        {mode === 'FLUX' ? 'Besoins en attente' : (mode === 'VALIDES' ? 'Besoins Validés' : mode === 'REJETES' ? 'Besoins Rejetés' : 'Archives Magasin')}
                    </h2>
                    <div style={{ display: 'flex', background: '#f1f5f9', padding: '4px', borderRadius: '8px', gap: '2px' }}>
                        {['FLUX', 'VALIDES', 'REJETES', 'ARCHIVES'].map(m => (
                            <button key={m} onClick={() => setMode(m)} style={{
                                padding: '8px 16px', border: 'none', borderRadius: '6px', cursor: 'pointer',
                                background: mode === m ? 'white' : 'transparent',
                                fontWeight: '600', fontSize: '12px', color: mode === m ? '#0f172a' : '#64748b',
                                boxShadow: mode === m ? '0 2px 4px rgba(0,0,0,0.05)' : 'none',
                                display: 'flex', alignItems: 'center', gap: '8px'
                            }}>
                                {m === 'FLUX' ? 'En attente' : m.charAt(0) + m.slice(1).toLowerCase()}
                                <span style={{ 
                                    background: mode === m ? primaryColor : '#cbd5e1', 
                                    color: 'white', padding: '1px 6px', borderRadius: '10px', fontSize: '10px' 
                                }}>
                                    {counts[m] || 0}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden', flexShrink: 0 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead style={{ background: '#f8fafc', color: '#64748b', fontSize: '12px' }}>
                            <tr>
                                <th style={{ padding: '12px', textAlign: 'left' }}>GROUPE</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>ID BESOIN / DATE</th>
                                <th style={{ padding: '12px', textAlign: 'left' }}>ARTICLE</th>
                                <th style={{ padding: '12px', textAlign: 'center' }}>QTÉ (VOLUME)</th>
                                <th style={{ padding: '12px', textAlign: 'center' }}>STATUT</th>
                                <th style={{ padding: '12px' }}>OBSERVATION</th>
                                <th style={{ padding: '12px', textAlign: 'right' }}>ACTION</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px' }}><Loader2 className="animate-spin mx-auto" /></td></tr>
                            ) : lignes.length === 0 ? (
                                <tr><td colSpan="7" style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Aucun dossier trouvé.</td></tr>
                            ) : lignes.map((item) => (
                                <tr key={item.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                    <td style={{ padding: '12px', fontWeight: '600', color: '#64748b', fontSize: '11px' }}>#{item.code_groupe_parent}</td>
                                    <td style={{ padding: '12px' }}>
                                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                                            <span style={{ fontWeight: 'bold', color: primaryColor, fontSize: '11px' }}>#{item.id}</span>
                                            <span style={{ fontSize: '10px', color: '#94a3b8' }}>{formaterDate(item.date_affichage)}</span>
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px', fontWeight: '500', fontSize: '13px' }}>{item.article_nom}</td>
                                    <td style={{ padding: '12px', textAlign: 'center', fontWeight: 'bold' }}>{item.quantite} <small>{item.unite}</small></td>
                                    <td style={{ padding: '12px', textAlign: 'center' }}>
                                        <span style={{ 
                                            padding: '4px 10px', borderRadius: '12px', fontSize: '10px', fontWeight: 'bold', 
                                            background: item.statut_ligne === 'VALIDE' ? '#dcfce7' : item.statut_ligne === 'REFUSE' ? '#fee2e2' : '#fff7ed', 
                                            color: item.statut_ligne === 'VALIDE' ? '#166534' : item.statut_ligne === 'REFUSE' ? '#991b1b' : '#c2410c' 
                                        }}>
                                            {item.statut_ligne || 'EN ATTENTE'}
                                        </span>
                                    </td>
                                    <td style={{ padding: '12px' }}>
                                        <div style={{ display: 'flex', gap: '5px' }}>
                                            <input type="text" value={observations[item.id] || ""} onChange={(e) => setObservations({...observations, [item.id]: e.target.value})} placeholder="Note..." style={{ flex: 1, padding: '6px', border: '1px solid #cbd5e1', borderRadius: '4px', fontSize: '11px' }} />
                                            <button onClick={() => majObservation(item.id, item.statut_ligne)} style={{ padding: '6px', background: '#475569', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}><Send size={12}/></button>
                                        </div>
                                    </td>
                                    <td style={{ padding: '12px', textAlign: 'right' }}>
                                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
                                            <button onClick={() => analyserMPLigne(item.id)} style={{ padding: '6px', background: '#0ea5e9', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' }} title="Analyse"><Beaker size={16}/></button>
                                            {mode === 'FLUX' && (
                                                <>
                                                    <button onClick={() => traiterLigne(item.id, 'REJETER')} style={{ padding: '6px', background: '#fee2e2', color: '#ef4444', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><X size={16}/></button>
                                                    <button onClick={() => traiterLigne(item.id, 'VALIDER')} style={{ padding: '6px', background: '#dcfce7', color: '#22c55e', border: 'none', borderRadius: '6px', cursor: 'pointer' }}><Check size={16}/></button>
                                                </>
                                            )}
                                            {mode === 'VALIDES' && (
                                                <button onClick={() => annulerValidation(item.id)} style={{ padding: '6px', background: '#fff7ed', color: '#d97706', border: '1px solid #fed7aa', borderRadius: '6px', cursor: 'pointer' }} title="Annuler"><RotateCcw size={16}/></button>
                                            )}
                                            {(mode === 'VALIDES' || mode === 'REJETES') && (
                                                <button onClick={() => executerTraitement(item.id, 'ARCHIVE')} style={{ padding: '6px', background: '#f1f5f9', color: '#64748b', border: 'none', borderRadius: '6px', cursor: 'pointer' }} title="Archiver"><Archive size={16}/></button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div ref={analyseRef} style={{ flexShrink: 0, paddingBottom: '20px' }}>
                    {compositionMP.length > 0 && (
                        <div style={{ background: 'white', borderRadius: '12px', boxShadow: '0 4px 15px rgba(0,0,0,0.05)', padding: '15px' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
                                <h3 style={{ margin: 0, fontSize: '15px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}><FileText size={18} color="#059669" /> Analyse des Stocks Réels & Lots</h3>
                                <button onClick={() => setCompositionMP([])} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer' }}><X size={18}/></button>
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <thead>
                                    <tr style={{ fontSize: '11px', color: '#64748b', textAlign: 'left', borderBottom: '2px solid #f1f5f9' }}>
                                        <th style={{ padding: '8px' }}>DÉSIGNATION</th>
                                        <th style={{ padding: '8px', textAlign: 'center' }}>REQUIS (PROPORTIONNEL)</th>
                                        <th style={{ padding: '8px', textAlign: 'center' }}>STOCK DISPO</th>
                                        <th style={{ padding: '8px', textAlign: 'center' }}>ÉCART</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {compositionMP.map((mp, i) => (
                                        <tr key={i} style={{ borderBottom: '1px solid #f1f5f9', fontSize: '13px' }}>
                                            <td style={{ padding: '8px', fontWeight: '500' }}>{mp.article_nom}</td>
                                            <td style={{ padding: '8px', textAlign: 'center', fontWeight: 'bold' }}>{mp.quantite_totale_requise} {mp.unite || ''}</td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>{mp.stock_actuel}</td>
                                            <td style={{ padding: '8px', textAlign: 'center' }}>
                                                <span style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '4px', fontWeight: 'bold', background: parseFloat(mp.ecart) >= 0 ? '#dcfce7' : '#fee2e2', color: parseFloat(mp.ecart) >= 0 ? '#166534' : '#991b1b' }}>
                                                    {parseFloat(mp.ecart) >= 0 ? `+ ${mp.ecart}` : mp.ecart}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VerificationMagasin;